import { doc, runTransaction, increment } from 'firebase/firestore';
import { usersRef } from './read';
import { db } from '../../config';
import { auditAction, auditResourceType, registerStatus } from '../../../../constants/enum';
import { handleDeleteUserFromAuth } from '../../function/handleDeleteUserFromAuth';
import { recordClientAuditEvent } from '../../function/security/clientAuditEventFunction';

/**
 * ユーザーを削除
 */
export const deleteUser = async (uid: string, loginUserUid: string): Promise<void> => {
  const userDocRef = doc(usersRef, uid);

  // トランザクションでFirestore操作を原子的に実行
  const { originalStatus, originalEmail, companyId } = await runTransaction(
    db,
    async transaction => {
      const userDoc = await transaction.get(userDocRef);
      if (!userDoc.exists()) {
        throw new Error('ユーザーが見つかりません。');
      }

      const userData = userDoc.data();
      const currentStatus = userData.register_status;
      const currentEmail = userData.email;
      const userCompanyId = userData.company_id as string | undefined;

      // ステータスを削除に変更
      transaction.update(userDocRef, {
        register_status: registerStatus.deleted,
        email: `${currentEmail}_${Date.now()}`,
        updated_at: new Date(),
        updated_by: loginUserUid,
      });

      // 企業の利用ユーザー数をデクリメント
      if (userCompanyId) {
        const companyDocRef = doc(db, 'companies', userCompanyId);
        transaction.update(companyDocRef, {
          current_user_count: increment(-1),
        });
      }

      return {
        originalStatus: currentStatus,
        originalEmail: currentEmail,
        companyId: userCompanyId,
      };
    },
  );

  // 監査ログ（#2985）: ユーザー削除（論理削除）。
  // 呼び出し元は削除完了後すぐ画面遷移するため、fire-and-forget にすると
  // 遷移で callable が中断されて記録が残らない（#3102）。await して完了を待つ。
  // recordClientAuditEvent は内部で例外を握りつぶすため、主処理は失敗させない。
  const recordUserDeleteAudit = async (): Promise<void> => {
    await recordClientAuditEvent({
      action: auditAction.delete,
      resource_type: auditResourceType.user,
      resource_id: uid,
    });
  };

  // メール招待中のユーザーはauthが存在しないため削除しない
  if (originalStatus === registerStatus.emailRegistered) {
    await recordUserDeleteAudit();
    return;
  }

  // Authからユーザーを削除（トランザクション外で実行）
  try {
    await handleDeleteUserFromAuth(uid);
  } catch {
    // Auth削除に失敗した場合、Firestoreの状態をロールバック
    await runTransaction(db, async transaction => {
      transaction.update(userDocRef, {
        register_status: originalStatus,
        email: originalEmail,
        updated_at: new Date(),
        updated_by: loginUserUid,
      });

      // デクリメントした利用ユーザー数を復元
      if (companyId) {
        const companyDocRef = doc(db, 'companies', companyId);
        transaction.update(companyDocRef, {
          current_user_count: increment(1),
        });
      }
    });
    throw new Error('ユーザーの削除に失敗しました。');
  }

  await recordUserDeleteAudit();
};
